import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import { OPTION_META_PREFIX } from './paths';
import type { OptionMetaOption, OptionMetaStore } from './types';

const DISPLAY_NAME_KEYS = ['alias', 'name', 'displayName', 'label', 'title'] as const;

/** @internal */
export function readString(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function parseJsonLike(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const text = value.trim();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return value;
  }
}

/** 提交值 → optionMeta 字典 key */
function buildOptionMetaKey(value: unknown): string | undefined {
  return readString(value);
}

/** 从字段值解析 optionMeta 查找 key（支持厂商设备等对象字段） */
export function resolveOptionMetaLookupKey(fieldValue: unknown, fallbackValue?: unknown): string | undefined {
  if (isPlainObject(fieldValue)) {
    const billItemId = readString(fieldValue.billItemId);
    if (billItemId) return billItemId;
    const vendorID = readString(fieldValue.vendorID);
    const deviceID = readString(fieldValue.deviceID);
    if (vendorID && deviceID) return `${vendorID}:${deviceID}`;
    const vendor = readString(fieldValue.vendor);
    const device = readString(fieldValue.device);
    if (vendor && device) return `${vendor}:${device}`;
    return buildOptionMetaKey(vendor ?? device ?? fieldValue.name);
  }
  return buildOptionMetaKey(fieldValue ?? fallbackValue);
}

/** 读取 form.optionMeta 字典 */
export function readOptionMetaStore(formValues: Record<string, unknown>): OptionMetaStore {
  const root = get(formValues, OPTION_META_PREFIX);
  if (!root || typeof root !== 'object' || Array.isArray(root)) return {};
  return root as OptionMetaStore;
}

/** 通过提交值读取完整 option：optionMeta[val] */
export function readOptionMetaByValue(store: OptionMetaStore, submittedValue: unknown): OptionMetaOption | undefined {
  const key = buildOptionMetaKey(submittedValue);
  if (!key) return undefined;
  const raw = store[key];
  return isPlainObject(raw) ? raw : undefined;
}

/** 通过源字段路径读取 option（先取字段提交值，再查 optionMeta[val]） */
export function readOptionMeta(formValues: Record<string, unknown>, sourceField: string, lookupKey?: string): OptionMetaOption | undefined {
  const fieldValue = get(formValues, sourceField);
  const key = lookupKey ?? resolveOptionMetaLookupKey(fieldValue);
  return readOptionMetaByValue(readOptionMetaStore(formValues), key);
}

/** 从 Select 选项构造完整 option 对象 */
export function buildOptionFromSelect(
  value: unknown,
  option?: unknown,
  listItems?: { value?: string; label?: string }[],
): OptionMetaOption | undefined {
  const selectedValue = readString(value);
  const opt = option as { label?: unknown; extra?: unknown } | undefined;
  const extraRaw = parseJsonLike(opt?.extra);
  const extra = isPlainObject(extraRaw) ? extraRaw : undefined;

  const labelFromOption = typeof opt?.label === 'string' ? readString(opt.label) : undefined;
  const displayName = readString(extra?.displayName);
  const metadata = isPlainObject(extra?.metadata) ? extra.metadata : undefined;
  const specObj = isPlainObject(extra?.spec) ? extra.spec : undefined;
  const listLabel = listItems?.find((item) => item.value === selectedValue)?.label;
  const name = readString(extra?.name ?? extra?.label ?? metadata?.name ?? displayName ?? labelFromOption ?? listLabel);
  const alias = readString(extra?.alias ?? specObj?.alias);

  if (!selectedValue && !name && !alias && !extra) return undefined;

  return {
    value: selectedValue,
    name: name ?? alias ?? labelFromOption ?? listLabel,
    label: name ?? displayName ?? labelFromOption ?? alias ?? listLabel,
    displayName,
    alias,
    ...extra,
  };
}

/** 从任意原始对象构造 option */
export function buildOptionFromRaw(value: unknown, raw?: Record<string, unknown>): OptionMetaOption | undefined {
  const selectedValue = readString(value);
  const name = readString(raw?.name ?? raw?.displayName ?? raw?.label ?? raw?.title ?? raw?.alias);
  if (!selectedValue && !name && !raw) return undefined;
  return {
    value: selectedValue,
    name: name ?? selectedValue,
    label: name,
    ...raw,
  };
}

/** 计价厂商设备 → option */
export function buildOptionFromVendorSelection(selection?: {
  vendor?: string;
  device?: string;
  vendorID?: string;
  deviceID?: string;
  billItemId?: string;
  quantityUnit?: string;
}): { key: string; option: OptionMetaOption } | undefined {
  if (!selection) return undefined;
  const vendor = readString(selection.vendor);
  const device = readString(selection.device);
  const name = [vendor, device].filter(Boolean).join(' / ') || undefined;
  const key =
    readString(selection.billItemId) ??
    (selection.vendorID && selection.deviceID ? `${selection.vendorID}:${selection.deviceID}` : undefined) ??
    (vendor && device ? `${vendor}:${device}` : undefined) ??
    vendor ??
    device;
  if (!key) return undefined;
  return {
    key,
    option: {
      value: key,
      name,
      label: name,
      displayName: device,
      vendor,
      device,
      vendorID: selection.vendorID,
      deviceID: selection.deviceID,
      billItemId: selection.billItemId,
      ...(selection.quantityUnit ? { quantityUnit: selection.quantityUnit } : {}),
    },
  };
}

/** 写入 optionMeta[lookupKey] = option */
export function buildOptionMetaPatch(
  submittedValue: unknown,
  option: OptionMetaOption | undefined,
  formValues?: Record<string, unknown>,
  lookupKey?: string,
): Record<string, unknown> {
  const key = lookupKey ?? buildOptionMetaKey(submittedValue);
  if (!key) return {};
  const current = formValues ? readOptionMetaStore(formValues) : {};
  if (!option) {
    const next = { ...current };
    delete next[key];
    return { [OPTION_META_PREFIX]: next };
  }
  return {
    [OPTION_META_PREFIX]: {
      ...current,
      [key]: option,
    },
  };
}

/** Select onChange：optionMeta[lookupKey] = option */
export function syncSelectOptionMeta(
  form: FormInstance,
  value: unknown,
  option?: unknown,
  listItems?: { value?: string; label?: string }[],
  lookupKey?: string,
): void {
  const built = buildOptionFromSelect(value, option, listItems);
  form.setFieldsValue(buildOptionMetaPatch(value, built, form.getFieldsValue() as Record<string, unknown>, lookupKey));
}

/** 非 Select 场景（Raw 对象 / 自定义 label）写入 optionMeta */
export function syncOptionMetaFromRaw(form: FormInstance, value: unknown, raw?: Record<string, unknown>, lookupKey?: string): void {
  const built = buildOptionFromRaw(value, raw);
  form.setFieldsValue(buildOptionMetaPatch(value, built, form.getFieldsValue() as Record<string, unknown>, lookupKey));
}

function readFormValues(source: FormInstance | Record<string, unknown>): Record<string, unknown> {
  if (typeof (source as FormInstance).getFieldsValue === 'function') {
    return (source as FormInstance).getFieldsValue() as Record<string, unknown>;
  }
  return source as Record<string, unknown>;
}

/** 按字段路径从 source 合并 optionMeta 到 target（读取各字段提交值作为 key） */
export function copyOptionMetaBetweenForms(
  targetForm: FormInstance,
  sourceForm: FormInstance | Record<string, unknown>,
  fieldPaths: string[],
): void {
  mergeOptionMetaFromFields(targetForm, readFormValues(sourceForm), fieldPaths);
}

function mergeOptionMetaFromFields(targetForm: FormInstance, sourceValues: Record<string, unknown>, fieldPaths: string[]): void {
  const sourceStore = readOptionMetaStore(sourceValues);
  const targetStore = readOptionMetaStore(targetForm.getFieldsValue() as Record<string, unknown>);
  const next = { ...targetStore };

  for (const path of fieldPaths) {
    const fieldValue = get(sourceValues, path);
    const key = resolveOptionMetaLookupKey(fieldValue);
    if (key && sourceStore[key]) {
      next[key] = sourceStore[key];
    }
  }

  if (Object.keys(next).length) {
    targetForm.setFieldsValue({ [OPTION_META_PREFIX]: next });
  }
}

/** 按提交值列表从 source 合并 optionMeta 到 target */
export function mergeOptionMetaForValues(
  targetForm: FormInstance,
  sourceValues: Record<string, unknown>,
  submittedValues: unknown[],
): void {
  const sourceStore = readOptionMetaStore(sourceValues);
  const targetStore = readOptionMetaStore(targetForm.getFieldsValue() as Record<string, unknown>);
  const next = { ...targetStore };

  for (const val of submittedValues) {
    const key = buildOptionMetaKey(val);
    if (key && sourceStore[key]) {
      next[key] = sourceStore[key];
    }
  }

  if (Object.keys(next).length) {
    targetForm.setFieldsValue({ [OPTION_META_PREFIX]: next });
  }
}

/** 展示文案：optionMeta[lookupKey].name 优先，再回退提交值 key，最后回退 fallback */
export function resolveOptionDisplayValue(
  formValues: Record<string, unknown> | undefined,
  sourceField: string,
  fallbackValue?: unknown,
  lookupKey?: string,
): string | undefined {
  if (!formValues) return readString(fallbackValue);
  const store = readOptionMetaStore(formValues);
  const fieldValue = get(formValues, sourceField);

  const readByKey = (key?: string) => (key ? readOptionMetaByValue(store, key) : undefined);

  let option = readByKey(lookupKey ?? resolveOptionMetaLookupKey(fieldValue, fallbackValue));

  if (!option && lookupKey) {
    option = readByKey(resolveOptionMetaLookupKey(fieldValue, fallbackValue));
  }

  if (!option) {
    option = readByKey(resolveOptionMetaLookupKey(fallbackValue));
  }

  if (option) {
    for (const key of DISPLAY_NAME_KEYS) {
      const text = readString(option[key]);
      if (text) return text;
    }
    const valueText = readString(option.value);
    if (valueText) return valueText;
  }

  return readString(fallbackValue);
}
